import { useEffect, useMemo } from 'react'
import { View, ActivityIndicator } from 'react-native'
import { useRouter, useLocalSearchParams, type Href } from 'expo-router'
import { useTheme } from '../../../theme/useTheme'
import { useZportiAdminAccess } from '../../hooks/useZportiAdminAccess'
import { useImpersonation, type StartImpersonationInput } from '../../hooks/useImpersonation'
import { useDialog } from '../../../shared/hooks/useDialog'
import { IMPERSONATION_QUERY, UNIMPERSONATE_PATH } from '../../constants/impersonationRoute.constants'
import { IMPERSONATION_URL_HANDLER_COPY } from '../../constants/impersonationUrlHandler.constants'
import {
  isImpersonationTargetUserIdFormat,
  isImpersonationTargetEmailFormat,
} from '../../helpers/impersonationTarget.helper'
import { getImpersonationUrlHandlerStyles } from './ImpersonationUrlHandler.styles'

function firstQueryValue(value: string | string[] | undefined): string | undefined {
  if (value === undefined) return undefined
  const v = Array.isArray(value) ? value[0] : value
  const t = typeof v === 'string' ? v.trim() : ''
  return t.length > 0 ? t : undefined
}

function toStartPayload(
  email: string | null | undefined,
  userId: string | null | undefined,
): StartImpersonationInput | 'invalid' | null {
  const e = email?.trim()
  const u = userId?.trim()
  if (e && isImpersonationTargetEmailFormat(e)) return { targetEmail: e }
  if (u && isImpersonationTargetUserIdFormat(u)) return { targetUserId: u }
  if (e || u) return 'invalid'
  return null
}

let lastConsumedImpersonateUrlKey: string | null = null

function urlDedupeKey(email: string | null | undefined, userId: string | null | undefined): string {
  return `${email ?? ''}\t${userId ?? ''}`
}

const APP_HOME_HREF = '/app' as Href

interface ImpersonationUrlHandlerProps {
  /** Where to land after a successful impersonation start. Defaults to `/app`. */
  successReplaceHref?: Href
  /** Where to land when the flow aborts (no/invalid params, denied, failed). Defaults to `/app`. */
  exitReplaceHref?: Href
}

/**
 * Headless route: `?email=`, `?userId=`, then replace to `successReplaceHref` (default `/app`).
 * Legacy `?end=1` redirects to `/app/unimpersonate`.
 */
export function ImpersonationUrlHandler({
  successReplaceHref = APP_HOME_HREF,
  exitReplaceHref = APP_HOME_HREF,
}: ImpersonationUrlHandlerProps = {}) {
  const theme = useTheme()
  const styles = useMemo(() => getImpersonationUrlHandlerStyles(theme), [theme])
  const router = useRouter()
  const params = useLocalSearchParams<{
    email?: string | string[]
    userId?: string | string[]
    end?: string | string[]
  }>()
  const { showAlert } = useDialog()
  const { isAdmin, loading } = useZportiAdminAccess()
  const { startImpersonation, isActive, busy } = useImpersonation()

  const emailParam = firstQueryValue(params[IMPERSONATION_QUERY.EMAIL])
  const userIdParam = firstQueryValue(params[IMPERSONATION_QUERY.USER_ID])
  const legacyEndParam = firstQueryValue(params.end)

  const hasLegacyEndIntent = legacyEndParam === '1' || legacyEndParam === 'true'
  const startPayload = useMemo(
    () => toStartPayload(emailParam, userIdParam),
    [emailParam, userIdParam],
  )

  useEffect(() => {
    if (hasLegacyEndIntent) {
      router.replace(UNIMPERSONATE_PATH as Href)
      return
    }

    if (!emailParam && !userIdParam) {
      router.replace(exitReplaceHref)
      return
    }

    if (startPayload === 'invalid') {
      void showAlert({
        title: 'Invalid link',
        message: IMPERSONATION_URL_HANDLER_COPY.INVALID_LINK_TARGET,
        type: 'danger',
      })
      router.replace(exitReplaceHref)
      return
    }

    if (startPayload === null) {
      router.replace(exitReplaceHref)
      return
    }

    if (!loading && !isAdmin) {
      void showAlert({
        title: 'Access denied',
        message: IMPERSONATION_URL_HANDLER_COPY.ACCESS_DENIED,
        type: 'danger',
      })
      router.replace(exitReplaceHref)
      return
    }

    if (loading || !isAdmin) return
    if (isActive || busy) return

    const dedupeKey = urlDedupeKey(emailParam, userIdParam)
    if (lastConsumedImpersonateUrlKey === dedupeKey) return

    lastConsumedImpersonateUrlKey = dedupeKey
    void (async () => {
      const ok = await startImpersonation(startPayload)
      if (!ok) {
        lastConsumedImpersonateUrlKey = null
        void showAlert({
          title: 'Error',
          message: IMPERSONATION_URL_HANDLER_COPY.START_FAILED,
          type: 'danger',
        })
        router.replace(exitReplaceHref)
        return
      }
      lastConsumedImpersonateUrlKey = null
      router.replace(successReplaceHref)
    })()
  }, [
    busy,
    emailParam,
    exitReplaceHref,
    hasLegacyEndIntent,
    isActive,
    isAdmin,
    loading,
    router,
    showAlert,
    startImpersonation,
    startPayload,
    successReplaceHref,
    userIdParam,
  ])

  return (
    <View style={styles.busyWrap}>
      <ActivityIndicator size="large" color={theme.primary} />
    </View>
  )
}
